import { useMemo, useState } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StringParam, useQueryParam } from 'use-query-params';
import { array, InferType, object, ObjectSchema, string } from 'yup';

import { CreateInvestmentPayload, SpaceReadModel, useGetSpacesByIdQuery } from '@client/api';
import {
  Button,
  CurrencyAdornment,
  CurrencyAutocomplete,
  Datepicker,
  EmptyPlaceholder,
  FormField,
  FormSubmitButton,
  getCurrencySymbol,
  GridCol,
  GridRow,
  Input,
  isDefinedString,
  isValidDefinedDate,
  Modal,
  ModalContent,
  ModalFooter,
  moneyValueSchema,
  openDateIntervalSchema,
  Skeleton,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  toISODateString,
  transformDateToIsoDateString,
  Typography,
  useFormAdd,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  InvestmentSpaceCreateFormData,
  investmentSpaceCreateSchema,
  InvestmentSpaceItemCreateForm,
} from './investment-space-item-create.form';

import { CloseRequestModal, SpacesAddModal } from '../../../../components';
import { useTenantSettingsDefaults } from '../../../../hooks';

type FormModel = CreateInvestmentPayload;

const schema: ObjectSchema<FormModel> = object({
  name: string().required(),
  purpose: string().nullable(),
  amount: moneyValueSchema.nullable().makeOptional(),
  duration: object({
    start: string().required().transform(transformDateToIsoDateString),
    end: string().required().transform(transformDateToIsoDateString),
  }),
  returnDuration: openDateIntervalSchema,
  spaces: array().of(investmentSpaceCreateSchema).required(),
});

type FormValues = InferType<typeof schema>;
type FormPath = FieldPath<FormModel>;
type FormDefaultValues = DeepPartial<FormValues>;

export type InvestmentCreateFormData = FormValues;

interface InvestmentCreateFormProps {
  title: React.ReactNode;
  onSubmit: (data: FormValues) => Promise<void>;
  onClose: () => void;
  open?: boolean;
}

export const InvestmentCreateForm = ({ open = true, title, onSubmit, onClose }: InvestmentCreateFormProps) => {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const [spaceId] = useQueryParam('spaceId', StringParam);
  const [buildingId] = useQueryParam('buildingId', StringParam);

  const {
    data: preselectedSpace,
    isFetching: isFetchingSpace,
    isSuccess: isSuccessSpace,
  } = useGetSpacesByIdQuery({ id: spaceId ?? '' }, { skip: spaceId == null, refetchOnMountOrArgChange: true });

  const { defaultCurrencyCode } = useTenantSettingsDefaults();

  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      name: '',
      purpose: '',
      amount: {
        amount: '' as any,
        currency: {
          code: defaultCurrencyCode ?? '',
        },
      },
      duration: {
        start: '',
        end: '',
      },
      returnDuration: {
        start: '',
        end: '',
      },
      spaces: [],
    }),
    [defaultCurrencyCode],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { isDirty },
    control,
  } = methods;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const durationStart = useWatch({ control, name: 'duration.start', defaultValue: '' }) as any as Date | string; // can either be a string or date because of the datepicker transforming it to a Date
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const durationEnd = useWatch({ control, name: 'duration.end', defaultValue: '' }) as any as Date | string; // can either be a string or date because of the datepicker transforming it to a Date

  const duration = useMemo(
    () => ({
      start: isValidDefinedDate(durationStart) ? toISODateString(durationStart) : undefined,
      end: isValidDefinedDate(durationEnd) ? toISODateString(durationEnd) : undefined,
    }),
    [durationStart, durationEnd],
  );

  const amountCurrencyCode = useWatch({ control, name: 'amount.currency.code' });
  const amountCurrencySymbol =
    isDefinedString(amountCurrencyCode) && isDefinedString(getCurrencySymbol(amountCurrencyCode))
      ? getCurrencySymbol(amountCurrencyCode)
      : undefined;

  /**
   * #########################################
   * Investment Spaces
   */

  const {
    fields: investmentSpaces,
    items: spaces,
    adding: addingSpaces,
    hasItems: hasSpaces,
    itemCount: spaceCount,
    enableAdding: handleOpenAddSpaces,
    disableAdding: handleCloseAddSpaces,
    setNewItems: handleAddSpaces,
    removeItemByFieldIndex: handleRemoveSpace,
    getItemByFieldIndex: getSpaceByFieldIndex,
  } = useFormAdd<InvestmentSpaceCreateFormData, SpaceReadModel>({
    control,
    name: 'spaces',
    mapItemToFormItem: (x): InvestmentSpaceCreateFormData => ({ spaceId: x.id, priceId: undefined }),
    itemEqualsFormItem: (a, x) => a.id === x.spaceId,
    formItemEqualsFormItem: (x, y) => x.spaceId === y.spaceId,
    preselectedItem: preselectedSpace,
    isFetchingPreselectedItem: isFetchingSpace,
    isFetchingPreselectedItemSuccessful: isSuccessSpace,
  });

  /**
   * #########################################
   */

  const [closingRequest, setClosingRequest] = useState<boolean>(false);

  const checkCloseRequest = () => {
    if (isDirty) {
      setClosingRequest(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCloseRequest = () => {
    onClose();
    setClosingRequest(false);
  };

  const handleCancelCloseRequest = () => {
    setClosingRequest(false);
  };

  return (
    <>
      <Modal open={open} appear size="lg" onClose={checkCloseRequest} title={title}>
        <FormProvider {...methods}>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <ModalContent className="divide-solid divide-y">
              {/* General information */}
              <article className="pb-8 px-4 space-y-4">
                <Typography variant="label">{tGlobal('investment.generalInformation')}</Typography>
                <GridRow cols={{ def: 1, sm: 4 }} spacingX={2} spacingY={4}>
                  <GridCol span={{ def: 1, sm: 4 }}>
                    <FormField<FormValues, FormPath>
                      name="name"
                      defaultValue={''}
                      required
                      label={tGlobal('investment.name')}
                      render={({ field }) => <Input {...field} />}
                    />
                  </GridCol>

                  <GridCol span={{ def: 1, sm: 4 }}>
                    <FormField<FormValues, FormPath>
                      name="purpose"
                      defaultValue={''}
                      required
                      label={tGlobal('investment.purpose')}
                      render={({ field }) => <Input {...field} />}
                    />
                  </GridCol>

                  <GridCol span={{ def: 1, sm: 3 }}>
                    <FormField<FormValues, FormPath>
                      name="amount.amount"
                      defaultValue={''}
                      required
                      label={tGlobal('investment.unitAmount')}
                      render={({ field }) => (
                        <CurrencyAdornment position="start" symbol={amountCurrencySymbol}>
                          <Input type="number" className="pl-6" {...field} />
                        </CurrencyAdornment>
                      )}
                    />
                  </GridCol>
                  <GridCol span={{ def: 1, sm: 1 }}>
                    <FormField<FormValues, FormPath>
                      name="amount.currency.code"
                      defaultValue={''}
                      required
                      label={tGlobal('investment.unitCurrency')}
                      render={({ field: { value, ref, ...field } }) => (
                        <CurrencyAutocomplete selected={value} defaultCurrencyCode={defaultCurrencyCode} {...field} />
                      )}
                    />
                  </GridCol>

                  <GridCol span={{ def: 1, sm: 2 }}>
                    <FormField<FormValues, FormPath>
                      name="duration.start"
                      required
                      label={tGlobal('investment.durationStart')}
                      render={({ field: { value, ...field } }) => <Datepicker selected={value} {...field} />}
                    />
                  </GridCol>
                  <GridCol span={{ def: 1, sm: 2 }}>
                    <FormField<FormValues, FormPath>
                      name="duration.end"
                      required
                      label={tGlobal('investment.durationEnd')}
                      render={({ field: { value, ...field } }) => <Datepicker selected={value} {...field} />}
                    />
                  </GridCol>

                  <GridCol span={{ def: 1, sm: 2 }}>
                    <FormField<FormValues, FormPath>
                      name="returnDuration.start"
                      required
                      label={tGlobal('investment.returnDurationStart')}
                      render={({ field: { value, ...field } }) => <Datepicker selected={value} {...field} />}
                    />
                  </GridCol>
                  <GridCol span={{ def: 1, sm: 2 }}>
                    <FormField<FormValues, FormPath>
                      name="returnDuration.end"
                      required
                      label={tGlobal('investment.returnDurationEnd')}
                      render={({ field: { value, ...field } }) => <Datepicker selected={value} {...field} />}
                    />
                  </GridCol>
                </GridRow>
              </article>

              {/**
               *
               * SPACES
               *
               */}

              {/* General information */}
              <article className="pt-5 pb-6 px-4">
                <div className="flex items-start">
                  <Typography variant="label" className="flex-1 mb-8">
                    <span>{tGlobal('investment.spaces')}</span>
                    {spaceCount != null && <span className="ml-1 font-light">({spaceCount})</span>}
                  </Typography>

                  <Button
                    color="primary-dark"
                    progressing={isFetchingSpace}
                    disabledVariant="dark"
                    size="sm"
                    onClick={handleOpenAddSpaces}
                  >
                    {tCommon('add')}
                  </Button>
                </div>

                {isFetchingSpace && <Skeleton className="h-20" />}

                {!isFetchingSpace && !hasSpaces && (
                  <EmptyPlaceholder>
                    {tCommon('noEntity', { entity: tGlobal('entity.investmentSpace', { context: 'plural' }) })}
                  </EmptyPlaceholder>
                )}

                {!isFetchingSpace && hasSpaces && (
                  <Table className="w-full" size="small" layout="fixed">
                    <TableHead>
                      <TableHeadRow>
                        <TableHeadCell className="w-[100%]">
                          {tGlobal('entity.investmentSpace', { context: 'short' })}
                        </TableHeadCell>
                      </TableHeadRow>
                    </TableHead>

                    <TableBody>
                      {investmentSpaces?.map((field, index) => (
                        <InvestmentSpaceItemCreateForm
                          key={field.id}
                          fieldIndex={index}
                          space={getSpaceByFieldIndex(index)}
                          onRemove={handleRemoveSpace}
                        />
                      ))}
                    </TableBody>
                  </Table>
                )}
              </article>
            </ModalContent>

            <ModalFooter>
              <FormSubmitButton type="submit">{tCommon('save')}</FormSubmitButton>
              <Button color="primary-dark" onClick={checkCloseRequest}>
                {tCommon('close')}
              </Button>
            </ModalFooter>

            <CloseRequestModal
              open={closingRequest}
              onConfirm={handleConfirmCloseRequest}
              onCancel={handleCancelCloseRequest}
            />
          </form>
        </FormProvider>
      </Modal>

      {addingSpaces && (
        <SpacesAddModal
          open={addingSpaces}
          selected={spaces}
          duration={duration}
          rootId={buildingId ?? undefined}
          onSubmit={handleAddSpaces}
          onClose={handleCloseAddSpaces}
        />
      )}
    </>
  );
};
